import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Material UI Packages. */
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Checkbox,
    Paper,
    IconButton,
    Box,
    Collapse,
    Typography,
    Autocomplete,
    Popper,
    TextField,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';

/** Material UI Icons. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Internal Components */
import { updateSocketData } from 'shared/lib/fetchSocket';

import 'shared/config/Styles.css';

/** How long consecutive toggles are merged into a single save (ms). */
const PRESET_EMIT_DEBOUNCE_MS = 300;

/** Upper bound on holding the local value while waiting for the save to land (ms). */
const PRESET_LOCAL_HOLD_MAX_MS = 10000;

/** Custom styled Popper for Autocomplete dropdown.
 * 프리셋 이름은 75자까지 길어질 수 있어 칸 너비로 다 담기지 않는다 — 목록 항목은
 * 줄바꿈으로 접어서 전문을 보여준다. */
const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: '13px',
    },
    '& .MuiAutocomplete-option': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
});

const UpdateApplySetting = (props) => {
    /** Get props from ShipmentDetail.js */
    const { row, tabConfig } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking = 0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** shipment progress status (inprogress/complete/dispute). */
    const shipmentType = useSelector((state) => state.progressValue);
    /** Preset data saved for each trade partner. */
    const presetData = useSelector((state) => state[tabConfig.presetData]);

    const currentPresetId = row.il_opus_reference_preset?.id;
    const clientId = row.il_trade_partner_id_client?.id;
    /** Preset saved on this shipment — the baseline for applying the server value. */
    const savedPreset = row.il_opus_reference_preset;

    /** A shipment whose reference has no preset row yet runs on the column defaults —
     * every step on, which is what the backend creates the moment anything reads it.
     * Showing an all-off table would be wrong twice: it misstates the setting in force,
     * and it leaves the operator able to switch steps on but never off. */
    const defaultPreset = useMemo(
        () => Object.fromEntries((tabConfig.checkboxKeys || []).map((key) => [key, true])),
        [tabConfig.checkboxKeys],
    );

    /** State for the preset table data. */
    const [preSet, setPreSet] = useState(savedPreset ? [savedPreset] : [defaultPreset]);
    /** Preset value selected through the dropdown. */
    const [selectValue, setSelectValue] = useState(null);
    /** Text shown in the dropdown input. Controlled so a cancelled clear can put the name back. */
    const [inputValue, setInputValue] = useState('');
    /** State for managing table visibility. */
    const [isTableVisible, setIsTableVisible] = useState(false);

    /** Preset list for the current trade partner. */
    const presetList = useMemo(
        () =>
            (presetData || [])
                .filter((item) => item.il_trade_partner_id === clientId)
                .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        [presetData, clientId],
    );
    /** Header names and per-column keys come only from tab config — not copied into state. */
    const columnNames = tabConfig.presetColumn;
    const checkboxKeys = tabConfig.checkboxKeys;

    /** Keys whose value on this shipment no longer matches the template it was applied from.
     * 항목을 하나 고쳐도 프리셋 이름은 남으므로(연결 유지), 이름만 보면 지금 설정이 템플릿과
     * 같은지 알 수 없다 — 다른 항목만 머리글 밑에 노란 선으로 짚어준다. 템플릿이 나중에
     * 수정된 경우에도 같은 표시가 뜬다("템플릿과 다르다"는 사실만 말한다).
     * 이 운송 모드 서브테이블이 없는 템플릿은 비교 근거가 없어 아무 표시도 하지 않는다. */
    const differingKeys = useMemo(() => {
        const appliedPresetId = preSet[0]?.il_preset_id || null;
        if (!appliedPresetId) return new Set();
        const applied = (presetData || []).find((item) => item.id === appliedPresetId);
        const templateFlags = applied?.[tabConfig.presetTableName];
        if (!templateFlags) return new Set();
        return new Set((checkboxKeys || []).filter((key) => !!preSet[0]?.[key] !== !!templateFlags[key]));
    }, [presetData, preSet, checkboxKeys, tabConfig.presetTableName]);

    const [hasUnsettledChange, setHasUnsettledChange] = useState(false);

    const pendingValueRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const holdTimerRef = useRef(null);
    const preSetRef = useRef(preSet);
    const isMountedRef = useRef(true);
    /** The dropdown input, so clearing can take the cursor out of it. */
    const presetInputRef = useRef(null);

    const applyPreSetLocally = useCallback((nextPreset) => {
        preSetRef.current = [nextPreset];
        setPreSet([nextPreset]);
    }, []);

    /** Stop holding the local value so the saved one is applied on the next render. */
    const releaseLocalHold = useCallback(() => {
        if (holdTimerRef.current) {
            clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        setHasUnsettledChange(false);
    }, []);

    /** The save was rejected — drop the local value now instead of waiting out the hold.
     * A newer change queued in the meantime wins, so leave the hold in place for it. */
    const revertToSavedPreset = useCallback(() => {
        if (!isMountedRef.current) return;
        if (pendingValueRef.current || debounceTimerRef.current) return;
        releaseLocalHold();
    }, [releaseLocalHold]);

    const flushPendingEmit = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        const pending = pendingValueRef.current;
        if (!pending) return;
        pendingValueRef.current = null;

        if (!tabConfig.updateReducer[0]) return;

        updateSocketData(
            dispatch,
            tabValue,
            'cell',
            'il_preset',
            tabConfig.updateReducer[0],
            {
                id: row.id,
                key: 'il_opus_reference_preset',
                value: pending,
            },
            undefined,
            revertToSavedPreset,
        );
    }, [dispatch, tabValue, tabConfig, row.id, revertToSavedPreset]);

    /** Holds the latest flush so unmount (closing the panel, opening another row) can call it.
     * Assigned in an effect rather than during render — a render may be thrown away. */
    const flushPendingEmitRef = useRef(flushPendingEmit);
    useEffect(() => {
        flushPendingEmitRef.current = flushPendingEmit;
    });

    /** Queue a save, keeping only the last value within the debounce window. */
    const scheduleEmit = useCallback((nextPreset) => {
        pendingValueRef.current = nextPreset;
        setHasUnsettledChange(true);

        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            flushPendingEmitRef.current();
        }, PRESET_EMIT_DEBOUNCE_MS);

        if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
        holdTimerRef.current = setTimeout(() => {
            holdTimerRef.current = null;
            setHasUnsettledChange(false);
        }, PRESET_LOCAL_HOLD_MAX_MS);
    }, []);

    /** A change still pending when the panel closes would be lost — emit the
     * remaining snapshot just before unmount. */
    useEffect(
        () => () => {
            isMountedRef.current = false;
            flushPendingEmitRef.current();
            if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
        },
        [],
    );

    /** Whether the value the server returned matches what the screen shows. */
    const presetMatchesLocal = useCallback(
        (serverPreset, localPreset) => {
            if (!serverPreset || !localPreset) return false;
            if ((serverPreset.il_preset_id || null) !== (localPreset.il_preset_id || null)) return false;
            return (checkboxKeys || []).every((key) => !!serverPreset[key] === !!localPreset[key]);
        },
        [checkboxKeys],
    );

    /** Apply the preset stored on the server to the screen. */
    useEffect(() => {
        if (!savedPreset) return;

        if (hasUnsettledChange) {
            if (!presetMatchesLocal(savedPreset, preSetRef.current[0])) return;
            releaseLocalHold();
            return;
        }

        preSetRef.current = [savedPreset];
        setPreSet([savedPreset]);
        const savedOption = (presetData || []).find((item) => item.id === savedPreset.il_preset_id) || '';
        setSelectValue(savedOption);
        setInputValue(savedOption?.name || '');
    }, [savedPreset, presetData, hasUnsettledChange, presetMatchesLocal, releaseLocalHold]);

    /**  Triggered when a user selects a new preset from the Autocomplete dropdown.
     * Loads the matching preset data and applies it to the row. */
    const handlePreSetChange = (newValue) => {
        // X로 지우면 이름과 함께 적용된 설정도 풀리므로 한 번 더 확인받는다. 취소하면 이름을 그대로 둔다.
        if (!newValue) {
            const confirmed = window.confirm('Do you want to clear the applied settings?');
            if (!confirmed) return;
            setSelectValue(null);
            setInputValue('');

            const base = preSetRef.current[0] || defaultPreset;
            const clearedPreset = { ...base, id: currentPresetId, il_preset_id: '' };
            applyPreSetLocally(clearedPreset);
            scheduleEmit(clearedPreset);

            Promise.resolve().then(() => {
                if (isMountedRef.current) presetInputRef.current?.blur();
            });
            return;
        }
        setSelectValue(newValue);
        setInputValue(newValue.name || '');
        if (!tabConfig.presetTableName) {
            console.error('Invalid tabValue or tableName not found');
            return;
        }

        // Retrieve data matching the selected preset.
        const matchedPreset = presetList?.find((item) => item.id === newValue.id)?.[tabConfig.presetTableName];
        if (!matchedPreset) {
            console.warn('No matched preset found for the selected value.');
            return;
        }
        const changedId = { ...matchedPreset, id: currentPresetId };
        applyPreSetLocally(changedId);
        scheduleEmit(changedId);
    };

    /**
     * Handler for modifying checkbox state.
     * Keep il_preset_id — 담당자가 적용했던 프리셋이 무엇이었는지는 항목을 하나 고쳐도
     * 남아 있어야 한다. 연결을 끊는 것은 X(handlePreSetChange)뿐이다.
     **/
    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        /** No preset row yet: emit the whole default set with this one flipped. The
         * backend resolves the reference from the shipment id and creates the row, so
         * sending the single key alone would echo back a full default row the screen
         * does not match — the local value would then be held until it times out. */
        const base = preSetRef.current[0] || defaultPreset;
        const updatedPreset = { ...base, [name]: checked };
        applyPreSetLocally(updatedPreset);
        scheduleEmit(updatedPreset);
    };

    /** Handle for managing container collapse. */
    const handleOpenList = () => {
        setIsTableVisible((prevVisible) => !prevVisible);
    };

    /** Reusable function to generate table cells with checkboxes. */
    const renderCheckboxCell = (name) => (
        <TableCell key={name} sx={{ textAlign: 'center' }}>
            <Checkbox
                name={name}
                checked={!!preSet[0]?.[name]}
                onChange={handleCheckboxChange}
                disabled={shipmentType === 'complete' ? true : false}
            />
        </TableCell>
    );

    return (
        <>
            <Box sx={{ mb: 1, display: 'flex', gap: '15px' }}>
                <Typography
                    sx={{
                        color: '#003a52',
                        fontSize: '13px',
                        fontWeight: 500,
                    }}
                >
                    Apply Setting
                </Typography>
                <Autocomplete
                    value={selectValue || ''}
                    onChange={(event, newValue) => handlePreSetChange(newValue)}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue, reason) => {
                        // 지우기는 확인을 거쳐 handlePreSetChange가 처리한다 — 여기서 먼저 비우면 취소해도 이름이 사라진다.
                        if (reason === 'clear') return;
                        setInputValue(newInputValue);
                    }}
                    options={presetList}
                    getOptionLabel={(option) => (option ? option.name : '')}
                    disabled={shipmentType === 'complete' ? true : false}
                    slotProps={{
                        popper: {
                            component: StyledPopper,
                        },
                    }}
                    sx={{
                        width: '300px',
                        height: '27px',
                        '& .MuiInputBase-input': {
                            fontSize: '13px',
                            height: '12px',
                        },
                    }}
                    renderInput={(params) => (
                        <TextField {...params} inputRef={presetInputRef} label="" size="small" />
                    )}
                />

                <IconButton onClick={handleOpenList}>
                    {isTableVisible ? (
                        <Tooltip title="Collapse">
                            <ExpandLessIcon />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Expand">
                            <ExpandMoreIcon />
                        </Tooltip>
                    )}
                </IconButton>
            </Box>
            <Collapse in={isTableVisible} sx={{ display: 'block', width: '100%' }}>
                <TableContainer component={Paper} sx={{ bgcolor: 'white', borderRadius: '10px', boxShadow: 'none' }}>
                    <Table sx={{ minWidth: 600 }} size="small">
                        <TableHead sx={{ bgcolor: '#f1f1f1' }}>
                            <TableRow>
                                {(columnNames || []).map((colName, index) => {
                                    const differs = differingKeys.has(checkboxKeys?.[index]);
                                    return (
                                        <TableCell
                                            key={colName}
                                            className={differs ? 'differs-header' : undefined}
                                            sx={{ textAlign: 'center' }}
                                        >
                                            {differs ? (
                                                <Tooltip title={'This item differs from the saved template.'}>
                                                    <Box component="span" aria-label={`${colName} differs`}>
                                                        {colName}
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                colName
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow key="1">{checkboxKeys?.map((key) => renderCheckboxCell(key))}</TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Collapse>
        </>
    );
};
export default React.memo(UpdateApplySetting);
